if (!customElements.get('custom-variant-picker')) {
  customElements.define(
    'custom-variant-picker',
    class CustomVariantPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.fitmentData = {};
        this.variantsBySku = new Map();
        this.selectedYear = '';
        this.selectedModel = '';
        this.selectedLocation = '';

        this.yearSelect = this.querySelector('[data-fitment-field="year"]');
        this.modelSelect = this.querySelector('[data-fitment-field="model"]');
        this.locationSelect = this.querySelector('[data-fitment-field="location"]');
        this.noteSelect = this.querySelector('[data-fitment-field="note"]');
        this.noteField = this.querySelector('[data-fitment-note-field]');
        this.noteLabel = this.querySelector('[data-fitment-note-label]');
        this.skuField = this.querySelector('[data-fitment-sku]');
        this.skuValue = this.querySelector('[data-fitment-sku-value]');
        this.defaultNoteLabel = this.noteLabel?.textContent?.trim() || 'Note';

        this.placeholder = {
          year: this.yearSelect?.dataset.placeholder || '',
          model: this.modelSelect?.dataset.placeholder || '',
          location: this.locationSelect?.dataset.placeholder || '',
          note: this.noteSelect?.dataset.placeholder || '',
        };

        this.buildVariantMap();
        this.addEventListener('change', this.onChange);
        this.initFitmentData();
      }

      get productForm() {
        return document.getElementById(this.dataset.productFormId);
      }

      get productInfo() {
        return this.closest('product-info');
      }

      buildVariantMap() {
        const variants = this.parseJsonScript('[data-variants]') || [];
        variants.forEach((variant) => {
          if (!variant?.sku) return;
          this.variantsBySku.set(String(variant.sku), variant);
        });
      }

      parseJsonScript(selector) {
        const script = this.querySelector(selector);
        if (!script) return null;

        const raw = script.textContent.trim();
        if (!raw || raw === 'null') return null;

        try {
          let data = JSON.parse(raw);
          if (typeof data === 'string') {
            data = JSON.parse(data);
          }
          return data;
        } catch (error) {
          console.error('Unable to parse fitment JSON', error);
          return null;
        }
      }

      async initFitmentData() {
        let data = this.parseJsonScript('[data-fitment-data]');

        if (!this.hasFitmentData(data) && this.dataset.fitmentApiUrl) {
          data = await this.fetchFitmentFallback();
        }

        if (!this.hasFitmentData(data)) {
          this.hidden = true;
          if (this.dataset.fitmentRequired === 'true') {
            this.productForm?.classList.remove('product-form--fitment-required');
            this.productInfo?.toggleAddButton(false);
          }
          return;
        }

        this.fitmentData = data;
        this.populateYears();
        this.yearSelect.disabled = false;
        this.productForm?.classList.add('product-form--fitment-required');
        this.setVariantInput('');
        this.setFitmentPending(true);
      }

      hasFitmentData(data) {
        return Boolean(data) && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0;
      }

      async fetchFitmentFallback() {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);

        try {
          const response = await fetch(this.dataset.fitmentApiUrl, {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
          if (!response.ok) return null;
          const payload = await response.json();
          return payload?.data ?? payload;
        } catch (error) {
          return null;
        } finally {
          clearTimeout(timeoutId);
        }
      }

      populateYears() {
        const years = Object.keys(this.fitmentData).sort((a, b) => Number(b) - Number(a) || String(b).localeCompare(String(a)));
        this.setOptions(this.yearSelect, years, this.placeholder.year);
      }

      onChange = (event) => {
        const field = event.target?.dataset?.fitmentField;
        if (!field) return;

        switch (field) {
          case 'year':
            this.onYearChange();
            break;
          case 'model':
            this.onModelChange();
            break;
          case 'location':
            this.onLocationChange();
            break;
          case 'note':
            this.onNoteChange();
            break;
        }
      };

      onYearChange() {
        this.selectedYear = this.yearSelect.value;
        this.resetField(this.modelSelect, this.placeholder.model);
        this.resetField(this.locationSelect, this.placeholder.location);
        this.resetNote();
        this.clearVariant();

        if (!this.selectedYear) return;

        const models = this.getModelNames(this.fitmentData[this.selectedYear]);
        this.setOptions(this.modelSelect, models, this.placeholder.model);
        this.modelSelect.disabled = models.length === 0;
      }

      onModelChange() {
        this.selectedModel = this.modelSelect.value;
        this.resetField(this.locationSelect, this.placeholder.location);
        this.resetNote();
        this.clearVariant();

        if (!this.selectedModel) return;

        const locations = Object.keys(this.getLocations());
        this.setOptions(this.locationSelect, locations, this.placeholder.location);
        this.locationSelect.disabled = locations.length === 0;
      }

      onLocationChange() {
        this.selectedLocation = this.locationSelect.value;
        this.resetNote();
        this.clearVariant();

        if (!this.selectedLocation) return;

        const locationData = this.getLocations()[this.selectedLocation];
        if (!locationData) return;

        if (locationData.isNote) {
          this.showNote(locationData);
          return;
        }

        this.selectVariantBySku(this.getFirstSku(locationData));
      }

      onNoteChange() {
        const option = this.noteSelect.selectedOptions[0];
        const sku = option?.dataset?.sku || this.noteSelect.value;

        if (!this.noteSelect.value) {
          this.clearVariant();
          return;
        }

        this.selectVariantBySku(sku);
      }

      showNote(locationData) {
        const noteName = this.getNoteName(locationData);
        if (this.noteLabel) this.noteLabel.textContent = noteName;
        if (this.noteSelect) this.noteSelect.name = `properties[${noteName}]`;

        const entries = Object.entries(locationData.skuData || {});
        const fragment = document.createDocumentFragment();
        fragment.appendChild(this.createOption('', this.placeholder.note));
        entries.forEach(([label, sku]) => {
          fragment.appendChild(this.createOption(label, label, { sku: String(sku) }));
        });

        this.noteSelect.replaceChildren(fragment);
        this.noteSelect.disabled = entries.length === 0;
        this.noteSelect.required = true;
        this.noteField?.classList.remove('hidden');
        this.noteField?.removeAttribute('hidden');
      }

      getNoteName(locationData) {
        const modelData = this.fitmentData?.[this.selectedYear]?.[this.selectedModel];
        const noteName = [locationData?.note_name, modelData?.note_name, modelData?.locations?.note_name].find((name) =>
          this.isNoteName(name)
        );
        return noteName || this.defaultNoteLabel;
      }

      isNoteName(name) {
        if (typeof name !== 'string') return false;
        const normalized = name.trim().toLowerCase();
        return normalized !== '' && normalized !== 'null' && normalized !== 'undefined';
      }

      getModelNames(yearData) {
        if (!yearData || typeof yearData !== 'object') return [];
        return Object.keys(yearData).filter((key) => key !== 'note_name' && this.isPlainObject(yearData[key]));
      }

      getLocations() {
        const modelData = this.fitmentData?.[this.selectedYear]?.[this.selectedModel];
        if (!this.isPlainObject(modelData) || !this.isPlainObject(modelData.locations)) return {};

        return Object.entries(modelData.locations).reduce((locations, [key, value]) => {
          if (key === 'note_name' || !this.isPlainObject(value)) return locations;
          locations[key] = value;
          return locations;
        }, {});
      }

      getFirstSku(locationData) {
        const skuData = locationData?.skuData;
        if (!this.isPlainObject(skuData)) return '';
        return String(Object.values(skuData)[0] || '');
      }

      isPlainObject(value) {
        return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
      }

      selectVariantBySku(sku) {
        const variant = this.variantsBySku.get(String(sku));
        if (!variant) {
          this.clearVariant();
          this.productInfo?.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
          return;
        }

        this.selectVariant(variant);
      }

      selectVariant(variant) {
        this.productInfo?.toggleAddButton(
          !variant.available,
          variant.available ? undefined : FoxTheme.variantStrings.soldOut
        );
        this.productForm?.setAttribute('data-fitment-complete', '');
        this.setVariantInput(variant.id);
        this.updateSkuDisplay(variant.sku);
        this.notifyFitmentGallery(variant.sku);

        if (typeof this.productInfo?.handleVariantIdChange === 'function') {
          this.productInfo.handleVariantIdChange(variant.id, this.activeFieldId);
        }
      }

      notifyFitmentGallery(sku) {
        const gallery = this.productInfo?.querySelector('media-gallery');
        if (typeof gallery?.applyFitmentFilter === 'function') {
          gallery.applyFitmentFilter(sku || '');
        }
      }

      get activeFieldId() {
        if (this.noteSelect && !this.noteSelect.disabled && !this.noteField?.classList.contains('hidden')) {
          return this.noteSelect.id;
        }
        return this.locationSelect?.id;
      }

      clearVariant() {
        this.productForm?.removeAttribute('data-fitment-complete');
        this.setVariantInput('');
        this.updateSkuDisplay('');
        this.notifyFitmentGallery('');
        this.productInfo?.toggleAddButton(true, FoxTheme.variantStrings.chooseOptions);
        this.productInfo?.updateURL?.(this.productInfo.dataset.url, null);

        if (FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.productInfo?.sectionId || this.dataset.section,
              html: document,
              variant: null,
            },
          });
        }
      }

      setVariantInput(variantId) {
        const input = this.productForm?.querySelector('[name="id"]');
        if (!input) return;
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateSkuDisplay(sku) {
        const hasSku = Boolean(sku);
        if (this.skuValue) this.skuValue.textContent = hasSku ? sku : '';
        this.skuField?.classList.toggle('hidden', !hasSku);
        this.skuField?.toggleAttribute('hidden', !hasSku);
      }

      setFitmentPending(isPending) {
        if (!isPending) return;
        this.productForm?.removeAttribute('data-fitment-complete');
        this.productInfo?.toggleAddButton(true, FoxTheme.variantStrings.chooseOptions);
      }

      resetField(select, placeholder) {
        if (!select) return;
        this.setOptions(select, [], placeholder);
        select.disabled = true;
      }

      resetNote() {
        this.resetField(this.noteSelect, this.placeholder.note);
        if (this.noteSelect) {
          this.noteSelect.name = 'properties[Note]';
          this.noteSelect.required = false;
        }
        if (this.noteLabel) this.noteLabel.textContent = this.defaultNoteLabel;
        this.noteField?.classList.add('hidden');
        this.noteField?.setAttribute('hidden', '');
      }

      setOptions(select, values, placeholder) {
        if (!select) return;
        const fragment = document.createDocumentFragment();
        fragment.appendChild(this.createOption('', placeholder));
        values.forEach((value) => fragment.appendChild(this.createOption(value, value)));
        select.replaceChildren(fragment);
        select.value = '';
      }

      createOption(value, label, dataset = {}) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        Object.entries(dataset).forEach(([key, val]) => {
          option.dataset[key] = val;
        });
        return option;
      }
    }
  );
}
